import { useState } from "react";
import Button from "../../../components/Button";
import { ProvisioningStep, ProvisioningUser, StepStatus } from "../types";
import {
  useMarkComplete,
  useRefreshBilling,
  useRetryStep,
} from "../hooks/useOnboardingTracking";
import { CodeSnippet } from "./CodeSnippet";
import {
  buildGtagSnippet,
  buildGtmBodySnippet,
  buildGtmHeadSnippet,
} from "./snippets";

const STEP_STATUS_STYLE: Record<
  StepStatus,
  { circle: string; text: string; symbol: string; label: string }
> = {
  COMPLETED: {
    circle: "bg-green-500 text-white border-green-500",
    text: "text-content-primary",
    symbol: "✓",
    label: "Completed",
  },
  IN_PROGRESS: {
    circle: "bg-blue-500 text-white border-blue-500 animate-pulse",
    text: "text-content-primary",
    symbol: "●",
    label: "In Progress",
  },
  PENDING: {
    circle: "bg-white text-neutral-400 border-neutral-300",
    text: "text-content-secondary",
    symbol: "○",
    label: "Pending",
  },
  FAILED: {
    circle: "bg-red-500 text-white border-red-500",
    text: "text-content-primary",
    symbol: "✕",
    label: "Failed",
  },
  AWAITING_MANUAL: {
    circle: "bg-orange-500 text-white border-orange-500",
    text: "text-content-primary",
    symbol: "⚑",
    label: "Awaiting Manual",
  },
  SKIPPED: {
    circle: "bg-neutral-200 text-neutral-500 border-neutral-300",
    text: "text-content-secondary",
    symbol: "–",
    label: "Skipped",
  },
};

function formatDuration(startedAt: string | null, completedAt: string | null) {
  if (!startedAt) return null;
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

function ManualInstructions({
  step,
  user,
}: {
  step: ProvisioningStep;
  user: ProvisioningUser;
}) {
  if (step.step === "AWAIT_SCRIPT_INSTALLATION") {
    if (user.isGa4Only) {
      const measurementId = user.ga4MeasurementId ?? "G-XXXXXXXX";
      return (
        <div className="text-xs text-content-primary mt-2 space-y-2">
          <div>
            <strong>1.</strong> Enter the measurement ID in the PMS booking
            engine analytics settings:
          </div>
          <div className="flex items-center gap-2 pl-4">
            <code className="bg-neutral-100 px-2 py-1 rounded font-mono">
              {measurementId}
            </code>
          </div>
          <div>
            <strong>2.</strong> Install the Google tag on every page of the
            hotel website (just before{" "}
            <code className="bg-neutral-100 px-1 rounded">&lt;/head&gt;</code>):
          </div>
          <CodeSnippet
            code={buildGtagSnippet(measurementId)}
            label="Paste just before </head>"
          />
        </div>
      );
    }
    const containerId = user.gtmContainerPublicId ?? "GTM-XXXXXXX";
    return (
      <div className="text-xs text-content-primary mt-2 space-y-2">
        <div>
          <strong>1.</strong> Paste the GTM container ID{" "}
          <code className="bg-neutral-100 px-1 rounded font-mono">
            {containerId}
          </code>{" "}
          into the PMS booking engine settings.
        </div>
        <div>
          <strong>2.</strong> Install the GTM snippet on every page of the
          hotel website. Paste this as high as possible in{" "}
          <code className="bg-neutral-100 px-1 rounded">&lt;head&gt;</code>:
        </div>
        <CodeSnippet
          code={buildGtmHeadSnippet(containerId)}
          label="Paste in <head>"
        />
        <div>
          <strong>3.</strong> Paste this immediately after the opening{" "}
          <code className="bg-neutral-100 px-1 rounded">&lt;body&gt;</code> tag:
        </div>
        <CodeSnippet
          code={buildGtmBodySnippet(containerId)}
          label="Paste after opening <body>"
        />
      </div>
    );
  }

  if (step.step === "CONFIGURE_CROSS_DOMAIN_TRACKING") {
    return (
      <div className="text-xs text-content-primary mt-2 space-y-2">
        <div>
          <strong>1.</strong> In <strong>GA4</strong> → Admin → Data Streams →
          (select the web data stream) → Configure tag settings → Configure
          your domains. Add the hotel website domain and the booking engine
          domain.
        </div>
        <div>
          <strong>2.</strong> In <strong>Google Ads</strong> → Goals →
          Conversions, find the imported GA4 purchase conversion and verify
          it's set to <strong>Primary</strong>.
        </div>
      </div>
    );
  }

  if (step.step === "SETUP_BILLING") {
    return (
      <div className="text-xs text-content-primary mt-2 space-y-2">
        <div>
          <strong>1.</strong> In{" "}
          <a
            href={
              user.googleAdsCustomerId
                ? `https://ads.google.com/aw/billing/summary?ocid=${user.googleAdsCustomerId}`
                : "https://ads.google.com"
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Google Ads → Billing → Summary
          </a>{" "}
          for customer{" "}
          <code className="bg-neutral-100 px-1 rounded font-mono">
            {user.googleAdsCustomerId ?? "(no customer id)"}
          </code>
          , choose existing payments profile{" "}
          <code className="bg-neutral-100 px-1 rounded font-mono">
            2947-9900-9905
          </code>
          .
        </div>
        <div>
          <strong>2.</strong> Click <strong>Refresh Billing</strong> below.
        </div>
      </div>
    );
  }

  return null;
}

function StepActions({
  step,
  user,
}: {
  step: ProvisioningStep;
  user: ProvisioningUser;
}) {
  const markComplete = useMarkComplete();
  const retry = useRetryStep();
  const refreshBilling = useRefreshBilling();
  const [confirm, setConfirm] = useState<"complete" | "retry" | null>(null);

  if (step.status === "AWAITING_MANUAL" && step.step === "SETUP_BILLING") {
    return (
      <div className="mt-3">
        <Button
          variant="default"
          size="sm"
          loading={refreshBilling.isPending}
          onClick={() => refreshBilling.mutate(user.userId)}
        >
          Refresh Billing
        </Button>
      </div>
    );
  }

  if (step.status === "AWAITING_MANUAL") {
    return (
      <div className="mt-3 flex gap-2 items-center flex-wrap">
        {confirm === "complete" ? (
          <>
            <span className="text-xs text-content-secondary">Confirm:</span>
            <Button
              variant="default"
              size="sm"
              loading={markComplete.isPending}
              onClick={() =>
                markComplete.mutate(
                  { userId: user.userId, step: step.step },
                  { onSuccess: () => setConfirm(null) },
                )
              }
            >
              Yes, mark complete
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="default"
            size="sm"
            onClick={() => setConfirm("complete")}
          >
            Mark as Complete
          </Button>
        )}
      </div>
    );
  }

  if (step.status === "FAILED") {
    return (
      <div className="mt-3 flex gap-2 items-center flex-wrap">
        {confirm === "retry" ? (
          <>
            <span className="text-xs text-content-secondary">Confirm:</span>
            <Button
              variant="default"
              size="sm"
              loading={retry.isPending}
              onClick={() =>
                retry.mutate(
                  { userId: user.userId, step: step.step },
                  { onSuccess: () => setConfirm(null) },
                )
              }
            >
              Yes, reset &amp; retry
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="default" size="sm" onClick={() => setConfirm("retry")}>
            Reset &amp; Retry Step
          </Button>
        )}
      </div>
    );
  }

  return null;
}

function StepRow({
  step,
  user,
  isLast,
}: {
  step: ProvisioningStep;
  user: ProvisioningUser;
  isLast: boolean;
}) {
  const style = STEP_STATUS_STYLE[step.status];
  const duration = formatDuration(step.startedAt, step.completedAt);
  const error = step.metadata?.error as string | undefined;
  const isActionable =
    step.status === "FAILED" || step.status === "AWAITING_MANUAL";

  return (
    <div className="flex gap-4 relative">
      {/* Vertical line + circle */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${style.circle}`}
        >
          {style.symbol}
        </div>
        {!isLast && (
          <div className="w-0.5 flex-1 bg-neutral-200 min-h-[24px]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-content-secondary">
              Step {step.order}
            </div>
            <div className={`font-semibold ${style.text}`}>{step.label}</div>
          </div>
          <div className="text-xs text-content-secondary text-right flex-shrink-0">
            <div>{style.label}</div>
            {duration && <div className="mt-0.5">{duration}</div>}
          </div>
        </div>

        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-900">
            {error}
          </div>
        )}

        <ManualInstructions step={step} user={user} />

        {isActionable && <StepActions step={step} user={user} />}
      </div>
    </div>
  );
}

export function VerticalStepper({ user }: { user: ProvisioningUser }) {
  return (
    <div>
      {user.steps.map((step, i) => (
        <StepRow
          key={step.step}
          step={step}
          user={user}
          isLast={i === user.steps.length - 1}
        />
      ))}
    </div>
  );
}
